import cookieCommons from 'common/cookieCommons';
import { QueryFunctionContext, QueryKey } from 'react-query';
import { convertToIPost } from '../../common/post/convertToIPost';
import { mapQueryKeyToSearchInGroupData } from '../../common/search/generateSearchApiQuery';
import {
  FEED_SEARCH_POST_PAGE_SIZE,
  FEED_USER_WALL_PAGE_SIZE,
  GROUP_POST_PAGE_SIZE,
} from '../../constant/numericParams';
import {
  EPostVoteType,
  INewPostDetailBE,
  IPostBE,
  IPostCommentListResponse,
  IPostCommentPayload,
  IPostEachCommentResponse,
  IPostUpdateCommentPayload,
  TPostInfinityQuery,
  TPostQuery,
} from '../../interfaces/post.interface';
import { tmsHTTPRequest } from '../../services/axios/httpRequest';
import { DEFAULT_COMMENT_LIST } from './comment.api';

const DEFAULT_POST_COMMENT_API_PREFIX = '/v1/posts';

const generateUserIdParam = () => {
  const accessToken = cookieCommons.getAccessToken();
  const tmsUserId = cookieCommons.getTmsUserId();
  accessToken ? tmsUserId : undefined;
  return accessToken && tmsUserId ? `&userId=${tmsUserId}` : '';
};

// comment api

export const postNewCommentToPost = (data: IPostCommentPayload) => {
  return tmsHTTPRequest.post(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${data.postId}/comments`,
    data
  );
};

export const putUpdatePostComment = (data: IPostUpdateCommentPayload) => {
  return tmsHTTPRequest.put(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${data.postId}/comments/${data.commentId}`,
    data
  );
};

export const deletePostComment = (postId: string, commentId: string) => {
  return tmsHTTPRequest.delete(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${postId}/comments/${commentId}`
  );
};

export const getEachPostListComments = ({
  pageParam = 1,
  queryKey,
}: QueryFunctionContext<any[], any>): Promise<IPostCommentListResponse> => {
  return tmsHTTPRequest.get(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/comments?PostId=${queryKey[1]}&PageNumber=${pageParam}&PageSize=${DEFAULT_COMMENT_LIST}`
  ) as unknown as Promise<IPostCommentListResponse>;
};

export const getEachPostCommentReplies = ({
  pageParam = 1,
  queryKey,
}: QueryFunctionContext<any[], any>): Promise<IPostCommentListResponse> => {
  return tmsHTTPRequest.get(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/comments/replies?ParentId=${queryKey[1]}&PageNumber=${pageParam}&PageSize=${DEFAULT_COMMENT_LIST}`
  ) as unknown as Promise<IPostCommentListResponse>;
};

export const getPostPinnedComment = (
  postId: string
): Promise<IPostEachCommentResponse> => {
  return tmsHTTPRequest.get(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${postId}/comments/pinned`
  ) as unknown as Promise<IPostEachCommentResponse>;
};

export const putVotePostComment = (commentId: string) => {
  return tmsHTTPRequest.put(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/comments/${commentId}/vote`,
    null
  );
};

export const putPostPinComment = (postId: string, commentPostId: string) => {
  return tmsHTTPRequest.put(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${postId}/comments/${commentPostId}/pin`,
    null
  );
};

// post api

export const postNewPost = (data: FormData) => {
  return tmsHTTPRequest.post(`${DEFAULT_POST_COMMENT_API_PREFIX}`, data, {
    headers: {
      'content-type': 'multipart/form-data',
    },
  });
};

export const getPostOfGroupFeed = (
  folderId: string,
  pageParam: number,
  pageSize: string | number
) => {
  return tmsHTTPRequest.get(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/folder/${folderId}?PageNumber=${pageParam}&PageSize=${
      pageSize || GROUP_POST_PAGE_SIZE
    }${generateUserIdParam()}`
  );
};

export const middlewarePostOfGroupFeed = (groupId: string) => {
  return (context: QueryFunctionContext<QueryKey>) => {
    const { pageParam = 1, queryKey } = context;
    return new Promise<TPostInfinityQuery>((res, _rej) => {
      getPostOfGroupFeed(
        groupId,
        pageParam,
        (queryKey[1] as number) || GROUP_POST_PAGE_SIZE
      )
        .then((_res: any) => {
          res({
            ..._res,
            data: _res.data.map((item: IPostBE) => convertToIPost(item)) ?? [],
          });
        })
        .catch((err) => _rej(err));
    });
  };
};

export const putVotePost = (data: { postId: string; type: EPostVoteType }) => {
  return tmsHTTPRequest.put(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${data.postId}/vote`,
    data
  );
};

export const postReadPost = (postId: string) => {
  return tmsHTTPRequest.post(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${postId}/read`,
    null
  );
};

export const deletePostHard = (postId: string) => {
  return tmsHTTPRequest.delete(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${postId}/hard`
  );
};

export const postIncreaseShareCount = (postId: string) => {
  return tmsHTTPRequest.post(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${postId}/share-count`,
    null
  );
};

export const putUpdatePost = (data: FormData) => {
  return tmsHTTPRequest.put(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${data.get('postId')}`,
    data,
    {
      headers: {
        'content-type': 'multipart/form-data',
      },
    }
  );
};

export const getVisitorFeed = (data: {
  hashtagIds: string[];
  postIds: string[];
}) => {
  return tmsHTTPRequest.post(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/visitor-feed?${generateUserIdParam()}`,
    data
  ) as unknown as Promise<IPostBE>;
};

export const middlewarePostOfVisitorFeed = (data: {
  hashtagIds: string[];
  postIds: string[];
}) => {
  return new Promise<TPostQuery>((res, _rej) => {
    getVisitorFeed(data)
      .then((_res: any) => {
        res(_res.map((item: IPostBE) => convertToIPost(item)) ?? []);
      })
      .catch((err) => _rej(err));
  });
};

export const getHomeFeed = () => {
  return tmsHTTPRequest.get(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/home?${generateUserIdParam()}`
  ) as unknown as Promise<IPostBE>;
};

export const middlewarePostOfHomeFeed = () => {
  return new Promise<TPostQuery>((res, _rej) => {
    getHomeFeed()
      .then((_res: any) => {
        res(_res.map((item: IPostBE) => convertToIPost(item)) ?? []);
      })
      .catch((err) => _rej(err));
  });
};

export const getPostSearchList = (keyword: string) => {
  return ({ pageParam = 1, queryKey }: QueryFunctionContext<any[], any>) => {
    const data = {
      pageNumber: pageParam,
      pageSize: queryKey[1] || FEED_SEARCH_POST_PAGE_SIZE,
      keyword: keyword,
    };
    return tmsHTTPRequest.post(
      `/v1/posts/search?${generateUserIdParam()}`,
      data
    );
  };
};

export const getPostDetail = (postId: string) => {
  return tmsHTTPRequest.get<INewPostDetailBE>(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${postId}?${generateUserIdParam()}`
  );
};

export const getPostInUserWall = (
  userId: string,
  pageParam: number = 1,
  pageSize: number = FEED_USER_WALL_PAGE_SIZE
) => {
  return tmsHTTPRequest.get(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/user/${userId}?PageNumber=${pageParam}&PageSize=${pageSize}`
  );
};

/** querykey must follow [KEY, userId, pageSize] */
export const middlewarePostOfUserWall = (
  context: QueryFunctionContext<QueryKey>
) => {
  const { pageParam = 1, queryKey } = context;
  return new Promise<TPostInfinityQuery>((res, _rej) => {
    getPostInUserWall(
      queryKey[1] as string,
      pageParam,
      (queryKey[2] as number) || FEED_USER_WALL_PAGE_SIZE
    )
      .then((_res: any) => {
        res({
          ..._res,
          data: _res.data.map((item: IPostBE) => convertToIPost(item)) ?? [],
        });
      })
      .catch((err) => _rej(err));
  });
};

export const postSearchPostInGroup = (
  context: QueryFunctionContext<QueryKey>
) => {
  const data = mapQueryKeyToSearchInGroupData(context);
  return tmsHTTPRequest.post(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${
      data.folderId
    }/search?${generateUserIdParam()}`,
    data
  );
};

export const deleteHardApplications = (data: {
  postId: string;
  applicationsId: string;
}) => {
  return tmsHTTPRequest.delete<string>(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/applications/${data.applicationsId}`
  );
};

export const postBookmarkPost = (postId: string) =>
  tmsHTTPRequest.post<string>(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${postId}/bookmark`,
    null
  );

export const getCheckBookmarkPost = (postId: string) =>
  tmsHTTPRequest.get<boolean>(
    `${DEFAULT_POST_COMMENT_API_PREFIX}/${postId}/bookmarked`
  );
